import { CognitoUser } from 'amazon-cognito-identity-js'
import { Alert, Form, FormButton, FormProps, Input } from 'admin-web-ui'
import { Store } from 'rc-field-form/lib/interface'
import React from 'react'
import { Link } from 'react-router-dom'
import { useAsyncFn } from 'react-use'
import styled from 'styled-components'
import { useAppContext } from '../../containers/App'
import { forgotPassword } from '../../utils/cognito'

const TypedForm: React.FC<FormProps> = (props) => <Form {...props} />
const StyledForm = styled(TypedForm)`
  max-width: 400px;
  text-align: left;
`

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const StyledLink = styled(Link)`
  font-weight: 600;
`

const StyledAside = styled.aside`
  background-color: ${({ theme }) => theme.greys.glamdring};
  border: 1px solid ${({ theme }) => theme.greys.geyser};
  border-radius: 2px;
`

const StyledEmailItem = styled(Form.Item)`
  margin-bottom: 8px;
`

const InfoText = styled.p`
  color: ${({ theme }) => theme.greys.fjord};
  font-size: 0.9em;
  padding: 8px;
  margin: 4px;
  line-height: 1.6;
`

const StyledAlert = styled(Alert)`
  margin-top: 10px;
`

interface Props {
  onSubmit: (email: string) => void
}

interface FormData {
  email: string
}

export const ForgotPasswordForm = (props: Props): JSX.Element => {
  const { onSubmit } = props
  const { cognitoUserPool } = useAppContext()

  const [submitState, submit] = useAsyncFn(
    async (store?: Store) => {
      if (!store) {
        return undefined
      }

      const { email } = store as FormData

      await forgotPassword({
        cognitoUser: new CognitoUser({
          Pool: cognitoUserPool,
          Username: email,
        }),
      })

      onSubmit(email)
      return true
    },
    [onSubmit, cognitoUserPool],
  )

  return (
    <StyledForm onFinish={submit}>
      <StyledEmailItem
        name="email"
        rules={[
          { type: 'email', message: 'Please enter a valid email address' },
          { required: true, message: 'Please input your email address' },
        ]}
      >
        <Input type="email" placeholder="Email address" />
      </StyledEmailItem>

      <InfoText>
        If there is an account matching this email, we’ll send a password reset
        email right away.
      </InfoText>

      <StyledAside>
        <InfoText>
          If you have never set your own password on this account then you will
          not receive an email. If so, contact your administrator to get a new
          account invitation email.
        </InfoText>
      </StyledAside>

      {submitState.error && (
        <StyledAlert
          type="error"
          message="An error occurred. Please try again."
        />
      )}

      <FooterContainer>
        <FormButton.Submit loading={submitState.loading}>
          Send Email
        </FormButton.Submit>
        <StyledLink to="/login">Back to Login</StyledLink>
      </FooterContainer>
    </StyledForm>
  )
}
